import type { TupleToUnion } from 'type-fest'
import { z } from 'zod'

import { generateRandomString, sequenceGenerator } from '@mntn-dev/utilities'
import type { ZodInfer } from '@mntn-dev/utility-types'

import type { AnyUrn } from '../types.ts'
import { OpaqueSchema, UniqueIdBuilder } from '../unique-id-builder.ts'
import { EmptyUuid } from './empty-uuid.ts'
import {
  NonEmptyStringSchema,
  PositiveIntegerSchema,
} from './property.models.ts'

export { UuidSchema } from '../unique-id-builder.ts'

/**
 * AgreementId
 */

export const [
  AgreementIdTag,
  AgreementIdSchema,
  AgreementId,
  AgreementUrnSchema,
  AgreementUrn,
] = UniqueIdBuilder('agreement')
export type AgreementId = ZodInfer<typeof AgreementIdSchema>
export type AgreementUrn = ZodInfer<typeof AgreementUrnSchema>

/**
 * ActivityId
 */

export const [
  ActivityIdTag,
  ActivityIdSchema,
  ActivityId,
  ActivityUrnSchema,
  ActivityUrn,
] = UniqueIdBuilder('activity')
export type ActivityId = ZodInfer<typeof ActivityIdSchema>
export type ActivityUrn = ZodInfer<typeof ActivityUrnSchema>

/**
 * AdvancePaymentId
 */
export const [
  AdvancePaymentIdTag,
  AdvancePaymentIdSchema,
  AdvancePaymentId,
  AdvancePaymentUrnSchema,
  AdvancePaymentUrn,
] = UniqueIdBuilder('advance-payment')
export type AdvancePaymentId = ZodInfer<typeof AdvancePaymentIdSchema>
export type AdvancePaymentUrn = ZodInfer<typeof AdvancePaymentUrnSchema>

/**
 * AgencyId
 */
export const [
  AgencyIdTag,
  AgencyIdSchema,
  AgencyId,
  AgencyUrnSchema,
  AgencyUrn,
] = UniqueIdBuilder('agency')
export type AgencyId = ZodInfer<typeof AgencyIdSchema>
export type AgencyUrn = ZodInfer<typeof AgencyUrnSchema>

/**
 * BidId
 */
export const [BidIdTag, BidIdSchema, BidId, BidUrnSchema, BidUrn] =
  UniqueIdBuilder('bid')
export type BidId = ZodInfer<typeof BidIdSchema>
export type BidUrn = ZodInfer<typeof BidUrnSchema>

/**
 * BillingProfileId
 */
export const [
  BillingProfileIdTag,
  BillingProfileIdSchema,
  BillingProfileId,
  BillingProfileUrnSchema,
  BillingProfileUrn,
] = UniqueIdBuilder('billing-profile')
export type BillingProfileId = ZodInfer<typeof BillingProfileIdSchema>
export type BillingProfileUrn = ZodInfer<typeof BillingProfileUrnSchema>

/**
 * BrandId
 */
export const [BrandIdTag, BrandIdSchema, BrandId, BrandUrnSchema, BrandUrn] =
  UniqueIdBuilder('brand')
export type BrandId = ZodInfer<typeof BrandIdSchema>
export type BrandUrn = ZodInfer<typeof BrandUrnSchema>

/**
 * CommentId
 */
export const [
  CommentIdTag,
  CommentIdSchema,
  CommentId,
  CommentUrnSchema,
  CommentUrn,
] = UniqueIdBuilder('comment')
export type CommentId = ZodInfer<typeof CommentIdSchema>
export type CommentUrn = ZodInfer<typeof CommentUrnSchema>

/**
 * DeliverableId
 */
export const [
  DeliverableIdTag,
  DeliverableIdSchema,
  DeliverableId,
  DeliverableUrnSchema,
  DeliverableUrn,
] = UniqueIdBuilder('deliverable')
export type DeliverableId = ZodInfer<typeof DeliverableIdSchema>
export type DeliverableUrn = ZodInfer<typeof DeliverableUrnSchema>

/**
 * DocumentId
 */
export const [
  DocumentIdTag,
  DocumentIdSchema,
  DocumentId,
  DocumentUrnSchema,
  DocumentUrn,
] = UniqueIdBuilder('document')
export type DocumentId = ZodInfer<typeof DocumentIdSchema>
export type DocumentUrn = ZodInfer<typeof DocumentUrnSchema>

/**
 * FeedId
 */
export const [FeedIdTag, FeedIdSchema, FeedId, FeedUrnSchema, FeedUrn] =
  UniqueIdBuilder('feed')
export type FeedId = ZodInfer<typeof FeedIdSchema>
export type FeedUrn = ZodInfer<typeof FeedUrnSchema>

/**
 * FileId
 */
export const [FileIdTag, FileIdSchema, FileId, FileUrnSchema, FileUrn] =
  UniqueIdBuilder('file')
export type FileId = ZodInfer<typeof FileIdSchema>
export type FileUrn = ZodInfer<typeof FileUrnSchema>

/**
 * InvoiceId
 */
export const [
  InvoiceIdTag,
  InvoiceIdSchema,
  InvoiceId,
  InvoiceUrnSchema,
  InvoiceUrn,
] = UniqueIdBuilder('invoice')
export type InvoiceId = ZodInfer<typeof InvoiceIdSchema>
export type InvoiceUrn = ZodInfer<typeof InvoiceUrnSchema>

/**
 * InvoiceLineItemId
 */
export const [
  InvoiceLineItemIdTag,
  InvoiceLineItemIdSchema,
  InvoiceLineItemId,
  InvoiceLineItemUrnSchema,
  InvoiceLineItemUrn,
] = UniqueIdBuilder('invoice-lineitem')
export type InvoiceLineItemId = ZodInfer<typeof InvoiceLineItemIdSchema>
export type InvoiceLineItemUrn = ZodInfer<typeof InvoiceLineItemUrnSchema>

/**
 * LocationId
 */
export const [
  LocationIdTag,
  LocationIdSchema,
  LocationId,
  LocationUrnSchema,
  LocationUrn,
] = UniqueIdBuilder('location')
export type LocationId = ZodInfer<typeof LocationIdSchema>
export type LocationUrn = ZodInfer<typeof LocationUrnSchema>

/**
 * ManualFinanceEntryId
 */
export const ManualFinanceEntryIdSchema = z.literal(EmptyUuid)
export type ManualFinanceEntryId = ZodInfer<typeof ManualFinanceEntryIdSchema>

export const ManualFinanceEntryId = () => EmptyUuid

/**
 * NetsuiteId
 */

export const NetsuiteIdPrefix = 'QF'
export const NetsuiteIdMin = 1
export const NetsuiteIdMax = 99999

const NetsuiteIdGenerator = sequenceGenerator(
  NetsuiteIdPrefix,
  NetsuiteIdMin,
  NetsuiteIdMax
)

const NetsuiteIdSequenceSchema = z.coerce
  .number()
  .int()
  .min(NetsuiteIdMin)
  .max(NetsuiteIdMax)

export const NetsuiteIdTag = 'netsuite'
export const NetsuiteIdSchema = OpaqueSchema(
  z
    .string()
    .regex(
      new RegExp(`^${NetsuiteIdPrefix}\\d{${NetsuiteIdMax.toString().length}}$`)
    )
    .transform((input) => input as ReturnType<typeof NetsuiteIdGenerator>)
    .refine(
      (input) =>
        NetsuiteIdSequenceSchema.safeParse(
          input.substring(NetsuiteIdPrefix.length)
        ).success
    ),
  NetsuiteIdTag
)

export const NetsuiteId = (sequence?: number) =>
  NetsuiteIdSchema.parse(NetsuiteIdGenerator(sequence))

export type NetsuiteId = ZodInfer<typeof NetsuiteIdSchema>

/**
 * NotificationId
 */
export const [
  NotificationIdTag,
  NotificationIdSchema,
  NotificationId,
  NotificationUrnSchema,
  NotificationUrn,
] = UniqueIdBuilder('notification')
export type NotificationId = ZodInfer<typeof NotificationIdSchema>
export type NotificationUrn = ZodInfer<typeof NotificationUrnSchema>

/**
 * NotificationPreferenceId
 */
export const [
  NotificationPreferenceIdTag,
  NotificationPreferenceIdSchema,
  NotificationPreferenceId,
  NotificationPreferenceUrnSchema,
  NotificationPreferenceUrn,
] = UniqueIdBuilder('notification-preference')
export type NotificationPreferenceId = ZodInfer<
  typeof NotificationPreferenceIdSchema
>
export type NotificationPreferenceUrn = ZodInfer<
  typeof NotificationPreferenceUrnSchema
>

/**
 * OrganizationId
 */
export const [
  OrganizationIdTag,
  OrganizationIdSchema,
  OrganizationId,
  OrganizationUrnSchema,
  OrganizationUrn,
] = UniqueIdBuilder('organization')
export type OrganizationId = ZodInfer<typeof OrganizationIdSchema>
export type OrganizationUrn = ZodInfer<typeof OrganizationUrnSchema>

/**
 * PartnerProgramId
 */
export const [
  PartnerProgramIdTag,
  PartnerProgramIdSchema,
  PartnerProgramId,
  PartnerProgramUrnSchema,
  PartnerProgramUrn,
] = UniqueIdBuilder('organization-program')
export type PartnerProgramId = ZodInfer<typeof PartnerProgramIdSchema>
export type PartnerProgramUrn = ZodInfer<typeof PartnerProgramUrnSchema>

/**
 * PackageId
 */
export const [
  PackageIdTag,
  PackageIdSchema,
  PackageId,
  PackageUrnSchema,
  PackageUrn,
] = UniqueIdBuilder('package')
export type PackageId = ZodInfer<typeof PackageIdSchema>
export type PackageUrn = ZodInfer<typeof PackageUrnSchema>

/**
 * PackageServiceId
 */
export const [
  PackageServiceIdTag,
  PackageServiceIdSchema,
  PackageServiceId,
  PackageServiceUrnSchema,
  PackageServiceUrn,
] = UniqueIdBuilder('package-service')
export type PackageServiceId = ZodInfer<typeof PackageServiceIdSchema>
export type PackageServiceUrn = ZodInfer<typeof PackageServiceUrnSchema>

/**
 * PlaceId
 */
export const PlaceIdTag = 'place'
export const PlaceIdSchema = OpaqueSchema(NonEmptyStringSchema(), PlaceIdTag)
export const PlaceId = (id: string) => PlaceIdSchema.parse(id)
export type PlaceId = ZodInfer<typeof PlaceIdSchema>

/**
 * ProjectId
 */
export const [
  ProjectIdTag,
  ProjectIdSchema,
  ProjectId,
  ProjectUrnSchema,
  ProjectUrn,
] = UniqueIdBuilder('project')
export type ProjectId = ZodInfer<typeof ProjectIdSchema>
export type ProjectUrn = ZodInfer<typeof ProjectUrnSchema>

/**
 * ProjectKey
 */
export const ProjectKeyPrefix = 'P-'
const ProjectKeyMin = 1
const ProjectKeyMax = 999999

const ProjectKeyGenerator = sequenceGenerator(
  ProjectKeyPrefix,
  ProjectKeyMin,
  ProjectKeyMax
)

const ProjectKeySequenceSchema = z.coerce
  .number()
  .int()
  .min(ProjectKeyMin)
  .max(ProjectKeyMax)

export const ProjectKeyTag = 'project-key'
export const ProjectKeySchema = OpaqueSchema(
  z
    .string()
    .regex(
      new RegExp(`^${ProjectKeyPrefix}\\d{${ProjectKeyMax.toString().length}}$`)
    )
    .transform((input) => input as ReturnType<typeof ProjectKeyGenerator>)
    .refine(
      (input) =>
        ProjectKeySequenceSchema.safeParse(
          input.substring(ProjectKeyPrefix.length)
        ).success
    ),
  ProjectKeyTag
)

export const ProjectKeySequence = (projectKey: ProjectKey) =>
  ProjectKeySequenceSchema.parse(
    ProjectKeySchema.parse(projectKey).substring(ProjectKeyPrefix.length)
  )

export const ProjectKey = (sequence?: number) =>
  ProjectKeySchema.parse(ProjectKeyGenerator(sequence))

export type ProjectKey = ZodInfer<typeof ProjectKeySchema>

/**
 * ProjectServiceId
 */
export const [
  ProjectServiceIdTag,
  ProjectServiceIdSchema,
  ProjectServiceId,
  ProjectServiceUrnSchema,
  ProjectServiceUrn,
] = UniqueIdBuilder('project-service')
export type ProjectServiceId = ZodInfer<typeof ProjectServiceIdSchema>
export type ProjectServiceUrn = ZodInfer<typeof ProjectServiceUrnSchema>

/**
 * ProofId
 */
export const [ProofIdTag, ProofIdSchema, ProofId, ProofUrnSchema, ProofUrn] =
  UniqueIdBuilder('proof')
export type ProofId = ZodInfer<typeof ProofIdSchema>
export type ProofUrn = ZodInfer<typeof ProofUrnSchema>

/**
 * ReviewId
 */
export const [
  ReviewIdTag,
  ReviewIdSchema,
  ReviewId,
  ReviewUrnSchema,
  ReviewUrn,
] = UniqueIdBuilder('review')
export type ReviewId = ZodInfer<typeof ReviewIdSchema>
export type ReviewUrn = ZodInfer<typeof ReviewUrnSchema>

/**
 * RoundId
 */
export const [RoundIdTag, RoundIdSchema, RoundId, RoundUrnSchema, RoundUrn] =
  UniqueIdBuilder('round')
export type RoundId = ZodInfer<typeof RoundIdSchema>
export type RoundUrn = ZodInfer<typeof RoundUrnSchema>

/**
 * ServiceId
 */

export const [
  ServiceIdTag,
  ServiceIdSchema,
  ServiceId,
  ServiceUrnSchema,
  ServiceUrn,
] = UniqueIdBuilder('service')
export type ServiceId = ZodInfer<typeof ServiceIdSchema>
export type ServiceUrn = ZodInfer<typeof ServiceUrnSchema>

/**
 * SessionId
 */

export const SessionIdTag = 'session' as const
export const SessionIdSchema = OpaqueSchema(
  NonEmptyStringSchema(),
  SessionIdTag
)
export type SessionId = ZodInfer<typeof SessionIdSchema>

const isSessionId = (value: unknown): value is SessionId => {
  return SessionIdSchema.safeParse(value).success
}

const EmptySessionId = '0000000000000000' as const

export const SessionId = Object.assign(
  (value = generateRandomString(EmptySessionId.length)) =>
    SessionIdSchema.parse(value),
  {
    Empty: SessionIdSchema.parse(EmptySessionId) as typeof EmptySessionId &
      z.BRAND<'session'>,
    check: isSessionId,
    parse: (input: AnyUrn | (string & z.BRAND<'session'>)) => {
      if (isSessionId(input)) {
        return input
      }

      const [, , sessionId] = input.split(':')

      if (isSessionId(sessionId)) {
        return sessionId
      }

      throw new Error(
        `Input '${input}' is not in the expected parse urn format.`
      )
    },
  }
)

/**
 * SurveyId
 */
export const [
  SurveyIdTag,
  SurveyIdSchema,
  SurveyId,
  SurveyUrnSchema,
  SurveyUrn,
] = UniqueIdBuilder('survey')
export type SurveyId = ZodInfer<typeof SurveyIdSchema>
export type SurveyUrn = ZodInfer<typeof SurveyUrnSchema>

/**
 * SurveyResponseId
 */
export const [
  SurveyResponseIdTag,
  SurveyResponseIdSchema,
  SurveyResponseId,
  SurveyResponseUrnSchema,
  SurveyResponseUrn,
] = UniqueIdBuilder('survey-response')
export type SurveyResponseId = ZodInfer<typeof SurveyResponseIdSchema>
export type SurveyResponseUrn = ZodInfer<typeof SurveyResponseUrnSchema>

/**
 * TagId
 */
export const [TagIdTag, TagIdSchema, TagId, TagUrnSchema, TagUrn] =
  UniqueIdBuilder('tag')
export type TagId = ZodInfer<typeof TagIdSchema>
export type TagUrn = ZodInfer<typeof TagUrnSchema>

/**
 * TeamId
 */
export const [TeamIdTag, TeamIdSchema, TeamId, TeamUrnSchema, TeamUrn] =
  UniqueIdBuilder('team')
export type TeamId = ZodInfer<typeof TeamIdSchema>
export type TeamUrn = ZodInfer<typeof TeamUrnSchema>

/**
 * TeamProfileId
 */
export const [
  TeamProfileIdTag,
  TeamProfileIdSchema,
  TeamProfileId,
  TeamProfileUrnSchema,
  TeamProfileUrn,
] = UniqueIdBuilder('team-profile')
export type TeamProfileId = ZodInfer<typeof TeamProfileIdSchema>
export type TeamProfileUrn = ZodInfer<typeof TeamProfileUrnSchema>

/**
 * ThreadId
 */
export const ThreadIdTag = 'thread'
export const ThreadIdSchema = OpaqueSchema(PositiveIntegerSchema, ThreadIdTag)
export type ThreadId = ZodInfer<typeof ThreadIdSchema>
export const ThreadId = (threadId: number) => ThreadIdSchema.parse(threadId)

/**
 * UbsId
 */
export const UbsIdTag = 'ubs'
export const UbsIdSchema = OpaqueSchema(NonEmptyStringSchema(), UbsIdTag)
export type UbsId = ZodInfer<typeof UbsIdSchema>
export const UbsId = (id: string = generateRandomString(10)) =>
  UbsIdSchema.parse(id)

/**
 * UserId
 */
export const [UserIdTag, UserIdSchema, UserId, UserUrnSchema, UserUrn] =
  UniqueIdBuilder('user')
export type UserId = ZodInfer<typeof UserIdSchema>
export type UserUrn = ZodInfer<typeof UserUrnSchema>

/**
 * SubjectId
 */
export const [SubjectIdTag, _subjectIdSchema, SubjectId] =
  UniqueIdBuilder('subject')
export const SubjectIdSchema = _subjectIdSchema
  .or(ProjectIdSchema)
  .or(FeedIdSchema)
export type SubjectId = ZodInfer<typeof SubjectIdSchema>

/**
 * TagListId
 */
export const TagListIdSchema = z.union([
  FileIdSchema,
  OrganizationIdSchema,
  PackageIdSchema,
  ProjectIdSchema,
  ServiceIdSchema,
  TeamIdSchema,
  UserIdSchema,
])
export type TagListId = ZodInfer<typeof TagListIdSchema>

export const idTags = [
  ActivityIdTag,
  AdvancePaymentIdTag,
  AgencyIdTag,
  AgreementIdTag,
  BidIdTag,
  BrandIdTag,
  CommentIdTag,
  DeliverableIdTag,
  DocumentIdTag,
  FeedIdTag,
  FileIdTag,
  InvoiceIdTag,
  InvoiceLineItemIdTag,
  LocationIdTag,
  NetsuiteIdTag,
  NotificationIdTag,
  NotificationPreferenceIdTag,
  OrganizationIdTag,
  PackageIdTag,
  PackageServiceIdTag,
  PlaceIdTag,
  ProjectIdTag,
  ProjectKeyTag,
  ProjectServiceIdTag,
  ProofIdTag,
  ReviewIdTag,
  RoundIdTag,
  ServiceIdTag,
  SessionIdTag,
  SubjectIdTag,
  SurveyIdTag,
  SurveyResponseIdTag,
  TagIdTag,
  TeamIdTag,
  TeamProfileIdTag,
  ThreadIdTag,
  UserIdTag,
] as const

export type IdTag = TupleToUnion<typeof idTags>
